import type { Step } from '$lib/model/types';
import { canonicalJson } from '$lib/util/canonicalJson';
import { sha256Hex } from '$lib/util/sha256';

// Step-hash helpers. Hashes are content-addresses used as cache keys.
//
// Preview fields are intentionally excluded from the hash so that changing
// an ink color, opacity, or ink mode does not invalidate the underlying
// pixel buffer.
//
// `children_count` IS included because some structural steps (notably
// `band`) emit different outputs based on whether they have any children:
//   - band with no children  → binary mask (printable as-is)
//   - band with children     → masked greyscale (so a halftone child can
//                              render real dot variation)
// Without this in the hash, the cache would serve the wrong variant after
// the user attaches a halftone child to an already-computed band.

export interface HashableStep {
  type: Step['type'];
  params: Step['params'];
}

export async function hashStep(
  step: Step,
  parentHash: string,
): Promise<string> {
  const payload: HashableStep & {
    parent_hash: string;
    children_count: number;
  } = {
    type: step.type,
    params: step.params,
    parent_hash: parentHash,
    children_count: step.children.length,
  };
  return await sha256Hex(canonicalJson(payload));
}

// Root parent hash: derived from the raw source image bytes. The input step
// itself is hashed against this as parent_hash, so changing the upload
// invalidates the entire downstream cache automatically.
export async function hashSourceBytes(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  return await sha256Hex(buf);
}
